import type { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, isCancel } from "axios";
import { isEqual } from "lodash-es";

import { truncate } from "../shared/utils";

import { localStateActions } from "./slices/local";
import { recordStoreApiCall, scheduleStoreSizeCheck } from "./store-log";

import { SERVER_API_PATH } from "api/api-constants";
import { serverApi } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { GlossaryKey } from "i18n/i18next";
import { viewStateActions } from "store/slices/view";
import { createReplaceAllReducer } from "store/utils";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { BROADCAST_TOPICS, addBroadcastListener } from "utils/broadcast";
import { rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { ModelType } from "utils/type-utils";

const ONE_HOUR_IN_MS = 3600 * 1000;
export const DEFAULT_CACHE_TIME_MS = ONE_HOUR_IN_MS;

/**
 * PubsubSlices internally cache API responses, which we sometimes want to invalidate
 * from an external source. Since the cache is stored internally, every slice invocation
 * saves a callback to clear its own cache here.
 */
const clearCacheCallbacks = new Set<() => void>();
export const clearPubsubAPICache = () => {
  clearCacheCallbacks.forEach((cb) => cb());
};

// None of the recommended types for empty model quite work here
// so we're just using an empty object for now.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type EmptyModel = {};

type OperationType = "create" | "update" | "destroy" | "retrieve" | "list" | "partial_update";

type OperationOptions = {
  skipToast?: boolean;
  skipCache?: boolean;
};

type CacheEntryType<T> = {
  ts: number;
  data: Promise<T | T[]>;
};

const storeOperationToToastKey: Record<OperationType, "update" | "load" | "delete" | "create"> = {
  create: "create",
  update: "update",
  destroy: "delete",
  retrieve: "load",
  list: "load",
  partial_update: "update",
};

/**
 * Creates a Redux-API-pubsub binding for a given model.
 * The binding consists of:
 * - A store slice mapping ID to model data.
 * - Reducers for adding/updating/removing model data from the slice.
 * - API methods for querying, getting, creating, updating, and removing model data.
 * - A pubsub listener setup that hooks into the Collaboration Websockets and forwards
 *   actions to the relevant reducers for the model. E.g. deleting a model on the backend
 *   will propagate through Collaboration and then delete the model in the store slice.
 *
 * @param viewsetPath The portion of the URL path that constitutes the "<app>/<viewset>"
 * part of URLs like "/api/v1/<app>/<viewset>/<...>". It will be used to construct the
 * URL for the request and also to infer a model name for use in naming the slice and
 * identifying the pubsub collection the slice should stay synced with.
 */
export const createPubsubSlice = <
  T extends ModelType & { section_version?: number; section_id?: string },
  L = EmptyModel,
  I = EmptyModel,
>(
  modelName: string,
  viewsetCustomPath?: string,
) => {
  type ListType = L;
  type InsertType = T & I;

  // We infer the viewset path from the model name, but allow it to be overridden if needed.
  const viewsetPath = viewsetCustomPath || `material/${modelName}`;

  // Prepare a base url path that is common to all the endpoints for the given viewset.
  const baseUrlPath = `${SERVER_API_PATH}/${viewsetPath}/`;

  // We can completely skip cached API calls, as we expect them to be updated
  // via pubsub after the original fetch. We still expire this cache with time,
  // in case the user returns to an inactive tab and has missed any websocket
  // messages. Once we have pubsub versioning, we can instead replay missed messages
  // on window focus or in intervals, and remove the time-based cache expiry.
  const apiCache = new Map<string, CacheEntryType<T>>();
  clearCacheCallbacks.add(() => apiCache.clear());

  const initialState = () => ({}) as Record<T["id"], T>;
  const slice = createSlice({
    name: modelName,
    initialState,
    reducers: {
      updateMany: (state, action: PayloadAction<T[]>) => {
        action.payload.forEach((item) => {
          if (!item?.id) {
            rollbarAndLogError(`Received ${modelName} without id in updateMany`, { item });
            return;
          }
          state[item.id] = item;
        });
      },
      updateOne: (state, action: PayloadAction<T>) => {
        if (!action.payload?.id) {
          rollbarAndLogError(`Received ${modelName} without id in updateOne`, {
            payload: action.payload,
          });
          return;
        }
        const existing = state[action.payload.id] as T;
        if (!isEqual(existing, action.payload)) {
          state[action.payload.id] = action.payload;
        }
      },
      patchOne: (state, action: PayloadAction<Partial<T>>) => {
        const id = action.payload?.id as string;
        if (!id) {
          rollbarAndLogError(`Received ${modelName} without id in patchOne`, {
            payload: action.payload,
          });
          return;
        }
        state[id] = { ...state[id], ...action.payload } as T;
      },
      removeOne: (state, action: PayloadAction<{ id: string }>) => {
        delete state[action.payload.id];
      },
    },
    extraReducers: createReplaceAllReducer<Record<T["id"], T>>(modelName, initialState),
  });

  // TODO: Make sure all models on the backend publish their updates using this schema
  const addListener = <P extends Dispatch>(dispatch: P) =>
    addBroadcastListener(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, (message) => {
      if (message.type !== "model-update" || message.model !== modelName) {
        return;
      }
      const newData = message.data as T;
      if (message.action === "create" || message.action === "update") {
        dispatch(slice.actions.updateOne(newData));
      } else if (message.action === "delete") {
        dispatch(slice.actions.removeOne(newData));
      }
    });

  const errorReporter =
    <P extends Dispatch>(
      action: OperationType,
      dispatch: P,
      options?: OperationOptions & { bustKey?: string; params?: Record<string, string> },
    ) =>
    (error: AxiosError | Error) => {
      // Don't log or surface canceled requests - these are expected when user is inactive and have
      // already been logged
      if (isCancel(error)) {
        throw error;
      }

      // TODO: We should use the skipToast option more often, possibly on all/most of the API
      //  use that isn't _directly_ related to user input
      if (!options?.skipToast) {
        const toastAction = storeOperationToToastKey[action];
        toastLocalizedOperationCatcher(`${toastAction}_failure`, {
          item: t(`glossary.${modelName as GlossaryKey}`),
        })(error);
      }

      // If an API call fails, we don't want to keep returning the cached error,
      // rather force a re-try on the next request.
      if (options?.bustKey) {
        apiCache.delete(options.bustKey);
      }

      const getErrorReporter = (err: AxiosError) => {
        if (
          err?.message &&
          (err.message === "Network Error" || err.message === "timeout exceeded")
        ) {
          return rollbarAndLogWarning;
        }
        // 401/403 are expected when unauthenticated or lacking permission; don't flood errors
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          return rollbarAndLogWarning;
        }
        return rollbarAndLogError;
      };

      function logAxiosErrorToRollbar(err: AxiosError) {
        if (!err.config) {
          rollbarAndLogError("unexpected err", err);
          return;
        }
        const queryParams = err.config.params as Record<string, string>;
        const stringParams = Object.entries(queryParams || {})
          .map(([key, value]) => `${key}=${value}`)
          .join("&");
        const requestString = `${err.config.url}?${stringParams}`;

        getErrorReporter(err)(`storeAPI axios error: ${err.message}`, err, {
          model: modelName,
          action,
          requestUrl: requestString,
          refererUrl: window.location.pathname + window.location.search,
          status: err.response?.status,
          body: truncate(err.response?.data, 200),
        });
      }

      function logGenericErrorToRollbar(err: Error) {
        const queryParams = options?.params;
        const stringParams = Object.entries(queryParams || {})
          .map(([key, value]) => `${key}=${value}`)
          .join("&");
        const requestString = `?${stringParams}`;
        rollbarAndLogError(`storeAPI generic error: ${err.message}`, err, {
          model: modelName,
          action,
          refererUrl: window.location.pathname + window.location.search,
          query: requestString,
        });
      }

      if (error instanceof AxiosError) {
        logAxiosErrorToRollbar(error);
      } else {
        logGenericErrorToRollbar(error);
      }
      dispatch(
        localStateActions.setApiError({ model: modelName, action, errorMessage: error.message }),
      );

      throw error;
    };

  // TODO: Loading/error state indicator for the API methods
  // TODO: Make sure all backend handlers support these actions
  // Note: While list/retrieve endpoints do return data directly, we prefer downstream consumers
  // to use the store to get the data, and not rely on the API call, where possible.
  const getApi = <P extends Dispatch>(dispatch: P) => {
    const updateAndReturn = (res: AxiosResponse<T>) => {
      const item = res.data;

      if ("section_version" in item) {
        dispatch(
          viewStateActions.lowerSectionVersionTo({
            results: [item],
            modelName,
          }),
        );

        // We remove the section_version from the results, as it's not relevant after this.
        if (modelName !== "sections") {
          delete item.section_version;
        }
      }

      dispatch(slice.actions.updateOne(item));
      return item;
    };

    const retrieve = (id: string) => {
      return serverApi.get<T>(`${baseUrlPath}${id}/`).then(updateAndReturn);
    };

    return {
      retrieve: (id: string, options?: OperationOptions) => {
        const cacheKey = id;
        let dataPromise: Promise<T> | undefined;
        if (cacheKey && !options?.skipCache && apiCache.has(cacheKey)) {
          const cacheEntry = apiCache.get(cacheKey);
          const isCacheValid = Date.now() - (cacheEntry?.ts || 0) < DEFAULT_CACHE_TIME_MS;
          if (cacheEntry && isCacheValid) {
            dataPromise = cacheEntry.data as Promise<T>;
          } else {
            apiCache.delete(cacheKey);
          }
        }

        if (!dataPromise) {
          dataPromise = retrieve(id).catch(
            errorReporter("retrieve", dispatch, { ...options, params: { id }, bustKey: cacheKey }),
          );
          if (cacheKey) {
            apiCache.set(cacheKey, { ts: Date.now(), data: dataPromise });
          }
        }

        return dataPromise.then((item) => {
          recordStoreApiCall(modelName, "retrieve", { id }, item);
          scheduleStoreSizeCheck();
          return item;
        });
      },

      list: (params?: Partial<ListType>, options?: OperationOptions) => {
        // Sanity check: passing a param with a null or undefined value is probably not
        // intentional and can cause unconstrained (i.e., runaway) queries in the back end.
        if (params !== undefined) {
          for (const key in params) {
            if (params[key] === null || params[key] === undefined) {
              // TODO: throw an exception in non-production environments.
              rollbarAndLogError("PubsubSlice.list was called with a null or undefined parameter", {
                url_path: baseUrlPath,
                param: key,
              });
            }
          }
        }

        // Check the cache for a result that matches the given params.
        const cacheKey = JSON.stringify(params || {});
        let dataPromise: Promise<T[]> | undefined;
        if (!options?.skipCache && apiCache.has(cacheKey)) {
          const cacheEntry = apiCache.get(cacheKey);
          const isCacheValid = Date.now() - (cacheEntry?.ts || 0) < DEFAULT_CACHE_TIME_MS;
          if (cacheEntry && isCacheValid) {
            dataPromise = cacheEntry.data as Promise<T[]>;
          } else {
            apiCache.delete(cacheKey);
          }
        }

        // We need to convert __in filters to comma-separated strings for the API.
        const modifiedParams = { ...params };
        for (const key in modifiedParams) {
          if (key.endsWith("__in") && Array.isArray(modifiedParams[key])) {
            modifiedParams[key] = modifiedParams[key].join(",") as L[Extract<keyof L, string>];
          }
        }

        if (!dataPromise) {
          dataPromise = serverApi
            .get<T[]>(baseUrlPath, {
              params: modifiedParams,
            })
            .then((res) => {
              let results = res.data;

              // A section's list call will contain a different section version per section, so we
              // don't want to update the current section version when we receive this data from
              // this list call.
              if (modelName !== "sections" && results?.length && "section_version" in results[0]) {
                dispatch(
                  viewStateActions.lowerSectionVersionTo({
                    results,
                    modelName,
                    params: modifiedParams,
                  }),
                );
                // We remove the section_version from the results, as it's not relevant after this.
                /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
                results = results.map(({ section_version, ...rest }) => rest) as T[];
              }

              dispatch(slice.actions.updateMany(results));
              return results;
            })
            .catch(
              errorReporter("list", dispatch, {
                ...options,
                params: params as Record<string, string>,
                bustKey: cacheKey,
              }),
            );

          apiCache.set(cacheKey, { ts: Date.now(), data: dataPromise });
        }

        return dataPromise.then((results) => {
          recordStoreApiCall(modelName, "list", params, results);
          scheduleStoreSizeCheck();
          return results;
        });
      },

      create: (data: Partial<Omit<InsertType, "id">>, options?: OperationOptions) => {
        // We don't currently support optimistic updates for create, because some fields
        // are defaulted on the server side that are locally required to render the item.
        // TODO: This is an anti-pattern and we should fix that separately before re-introducing
        // optimistic updates for create.
        return serverApi
          .post<T>(baseUrlPath, data)
          .then(updateAndReturn)
          .catch(errorReporter("create", dispatch, { ...options }));
      },

      // (Peter): There's currently no use case for an "update" operation, as we always use
      // partial_update, and PUT operations are needlessly dangerous.

      partial_update: (item: Partial<InsertType> & ModelType, options?: OperationOptions) => {
        const id = item.id;
        dispatch(slice.actions.patchOne(item));
        return serverApi
          .patch<T>(`${baseUrlPath}${id}/`, item)
          .then(updateAndReturn)
          .catch((err: AxiosError | Error) => {
            // Refresh to remove the optimistic update on failure
            retrieve(id).catch(
              errorReporter("partial_update", dispatch, { params: { id }, skipToast: true }),
            );
            errorReporter("partial_update", dispatch, options)(err);
          });
      },

      destroy: (id: string, options?: OperationOptions) => {
        dispatch(slice.actions.removeOne({ id }));
        return serverApi
          .delete(`${baseUrlPath}${id}/`)
          .then(() => dispatch(slice.actions.removeOne({ id })))
          .catch((err: AxiosError | Error) => {
            // Refresh to remove the optimistic update on failure
            retrieve(id).catch(
              errorReporter("destroy", dispatch, { skipToast: true, params: { id } }),
            );
            errorReporter("destroy", dispatch, options)(err);
          });
      },
    };
  };

  return { slice, addListener, getApi, actions: slice.actions };
};
